/**
 * Bridge a customer document into the Document Workbench store (TASK-449).
 *
 * Customer documents live in the data-ingestion service (`client_documents`,
 * `kielis-documents` bucket); the workbench reads from the document-export
 * service (`document_versions`, `kielis-exports` bucket). They are different
 * stores, so "Edit in Workbench" performs a one-time client-side copy:
 *   download the customer doc bytes → PUT them as workbench v1.
 *
 * The customer document's UUID is reused as the workbench `document_id`, which
 * keeps the mapping obvious and makes the import idempotent (GET-first).
 */

import { AxiosError } from 'axios';

import { getDocument, saveDocument, type WorkbenchFormat } from './documentApi';
import { documentService } from '../documentService';

const isStatus = (err: unknown, status: number): boolean =>
  err instanceof AxiosError && err.response?.status === status;

/**
 * Ensure `docId` exists in the workbench store, importing it from the customer
 * document store on first use. Safe to call repeatedly.
 *
 * @param docId  The customer document UUID (reused as the workbench document id).
 * @param format The document format ('docx' default, 'xlsx' for spreadsheets) —
 *               determines the MIME the seed version is stored under.
 */
export async function ensureWorkbenchDocument(
  docId: string,
  format: WorkbenchFormat = 'docx',
): Promise<void> {
  // Already imported? Nothing to do.
  try {
    await getDocument(docId);
    return;
  } catch (err) {
    if (!isStatus(err, 404)) throw err;
  }

  // Pull the source bytes from the customer document store and seed workbench v1.
  // Fetch directly from MinIO via a presigned URL (browser → MinIO) — the same
  // path the document/PDF viewers use. This deliberately avoids the gateway's
  // `/api/documents/{id}/download` proxy, which decodes binary as UTF-8 and 500s.
  const { url } = await documentService.getPresignedUrl(docId);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch source document (${res.status})`);
  }
  const blob = await res.blob();
  try {
    await saveDocument(docId, blob, undefined, format); // no X-Expected-Version → creates version 1
  } catch (err) {
    // A concurrent click may have created v1 first — that's a success for us.
    if (isStatus(err, 409)) return;
    throw err;
  }
}
